// The one risk assessment standard (REPORT-ra-organise.md part B) and the list page's organising rules (part A), for every RA:
// library, AI-generated and manual. Plain JS, no fetching: the page, the PDF (raPdf.js), the AI (api/_agent.js, api/ai.js),
// the library (raLibrary.js) and scripts/import-ra.mjs use it. Tests: tests/ra-organise.test.mjs.
//   category / department   one of RA_CATEGORIES / RA_DEPARTMENTS (columns of sql/ra-organise.sql; filled by keyword when empty -
//                           the SQL fills existing rows with the same rules, CAT_RULES / DEPT_RULES)
//   next review             the Master's review + 12 months (reviewed_at in the ship's time zone), else next_review_due
//   RA number               the next free "RA-NN" of the vessel (sql/ra-organise.sql gives it to every RA saved without one)
//   regulatory basis        { international_uk: [codes], flag_state: [codes] } - only codes of the regulations library, the flag
//                           state ones from the vessel's flag
//   related documents       RD-1 ... : { kind, title, note, q?, code?, id? } -> a link into Hey Captain where the target exists
import { toShipTime } from './shipTime.js'
import { hazardRisk, hazardCode } from './raMatrix.js'

export const RA_CATEGORIES = ['Deck & lifting', 'Tenders & water sports', 'Engineering', 'Hazardous substances', 'Emergencies & navigation', 'People & health', 'Other']
export const RA_DEPARTMENTS = ['Deck', 'Engine', 'Interior', 'Water sports', 'All']
export const ROLES = ['Captain', 'Chief Officer', 'Chief Engineer', 'Officer of the Watch', 'Bosun', 'DPA']
export const REVIEW_MONTHS = 12
export const DRAFT_STATUS = "Draft - needs Master's review"

// keyword rules, first match wins. Written with \b so the SQL (\y) runs the very same patterns (sql/ra-organise.sql, a test compares)
export const CAT_RULES = [
  ['Hazardous substances', '\\b(enclosed|confined|tanks?|voids?|cofferdams?|chemicals?|coshh|lithium|batter(y|ies)|bunker(ing)?|fuel(ling)?|paint(ing)?|solvents?|gas(es)?|refrigerants?|sewage|asbestos|toxic)\\b'],
  ['Tenders & water sports', '\\b(tenders?|ribs?|jet ?skis?|jetskis?|seabobs?|e-?foils?|water ?sports?|toys?|div(e|es|ing|ers?)|snorkel(ling)?|swim(ming)?|kayaks?|paddle ?boards?|wakeboard(ing)?|tow(ing)?)\\b'],
  ['Emergencies & navigation', '\\b(rescue|mob|man overboard|fire|abandon|emergenc(y|ies)|drills?|navigation|passage|pilotage|helicopters?|heli|storms?|heavy weather|grounding|collision|security|piracy|blackout)\\b'],
  ['Deck & lifting', '\\b(aloft|masts?|height|cranes?|davits?|lift(s|ing)?|rigging|moor(ing)?|anchor(ing)?|windlass|capstans?|overside|passerelle|gangway|deck|bosun|staging)\\b'],
  ['Engineering', '\\b(engines?|generators?|gensets?|electrical|electricity|isolation|alarms?|monitoring|machinery|pumps?|boilers?|compressors?|workshop|hydraulics?|steering|hot work|welding|grinding|ams|ums|shore power)\\b'],
  ['People & health', '\\b(guests?|persons?|passengers?|visitors?|contractors?|pregnan(t|cy)|expectant|mothers?|young|fatigue|manual handling|galley|food|laundry|medical|health|lone|new crew|familiari[sz]ation|heat)\\b'],
]
export const DEPT_RULES = [
  ['Water sports', '\\b(water ?sports?|seabobs?|e-?foils?|jet ?skis?|jetskis?|toys?|div(e|es|ing|ers?)|snorkel(ling)?|kayaks?|paddle ?boards?|wakeboard(ing)?|swim(ming)?)\\b'],
  ['Engine', '\\b(engines?|generators?|gensets?|electrical|electricity|isolation|alarms?|monitoring|machinery|pumps?|boilers?|compressors?|bunker(ing)?|fuel(ling)?|tanks?|enclosed|confined|voids?|workshop|hydraulics?|hot work|welding|grinding|steering|batter(y|ies)|lithium|ams|ums|shore power)\\b'],
  ['Interior', '\\b(galley|laundry|housekeeping|interior|cabins?|pantry|food|cleaning|stewards?|stewardess(es)?|chef)\\b'],
  ['Deck', '\\b(deck|aloft|masts?|cranes?|davits?|moor(ing)?|anchor(ing)?|windlass|capstans?|tenders?|ribs?|tow(ing)?|rescue|overside|gangway|passerelle|paint(ing)?|rigging|lift(s|ing)?|bosun|navigation|passage|helicopters?)\\b'],
]
// the activity decides; only when it matches no rule, the activity + scope
const firstRule = (rules, text) => { for (const [v, re] of rules) if (new RegExp(re, 'i').test(text)) return v; return '' }
const byWords = (rules, r) => firstRule(rules, String(r.activity || '')) || firstRule(rules, [r.activity, r.scope].filter(Boolean).join(' '))
export const autoCategory = r => byWords(CAT_RULES, r) || 'Other'
export const autoDepartment = r => byWords(DEPT_RULES, r) || 'All'
// the category / department an RA shows: its own, else by keyword
export const raCategory = r => (RA_CATEGORIES.includes(r && r.category) ? r.category : autoCategory(r || {}))
export const raDepartment = r => (RA_DEPARTMENTS.includes(r && r.department) ? r.department : autoDepartment(r || {}))

// ---- dates: always the ship's time zone ----
// a timestamp (reviewed_at) -> 'YYYY-MM-DD' on board; a plain date stays as it is
export function shipDate(v, tz) {
  if (!v) return ''
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const d = new Date(s)
  return isNaN(d) ? s.slice(0, 10) : toShipTime(d, tz || 'UTC').date
}
// 'YYYY-MM-DD' + n months, the day kept (the 31st of a short month -> its last day)
export function plusMonths(iso, n) {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number)
  if (!y || !m || !d) return ''
  const t = new Date(Date.UTC(y, m - 1 + n, 1))
  const last = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + 1, 0)).getUTCDate()
  return t.getUTCFullYear() + '-' + String(t.getUTCMonth() + 1).padStart(2, '0') + '-' + String(Math.min(d, last)).padStart(2, '0')
}
// the next review: the Master's review (on board) + 12 months; an RA not reviewed yet keeps its own next_review_due
export function nextReview(r, tz) {
  if (r && r.reviewed_at) return plusMonths(shipDate(r.reviewed_at, tz), REVIEW_MONTHS)
  return r && r.next_review_due ? String(r.next_review_due).slice(0, 10) : ''
}
export const isOverdue = (r, tz, today) => { const n = nextReview(r, tz); return !!n && !!today && n < today }
// 12 Mar 2026
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const niceDate = iso => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || '')); return m ? Number(m[3]) + ' ' + MON[Number(m[2]) - 1] + ' ' + m[1] : '' }

// ---- status ----
export const statusKind = s => { const t = String(s || '').toLowerCase().trim(); return t === 'active' ? 'active' : t === 'archived' ? 'archived' : /needs master|review/.test(t) ? 'review' : 'draft' }
export const STATUS_LABEL = { review: 'Needs review', active: 'Active', draft: 'Draft', archived: 'Archived' }
export const isLibrary = r => /^library/.test(String(r && r.source || ''))

// ---- RA numbers: the next free RA-NN of the vessel (the same rule as sql/ra-organise.sql ra_next_number) ----
export function nextRaNumber(numbers) {
  let max = 0
  for (const n of numbers || []) { const m = /^RA-(\d+)$/i.exec(String(n || '').trim()); if (m) max = Math.max(max, Number(m[1])) }
  return 'RA-' + String(max + 1).padStart(2, '0')
}

// ---- search / filters ----
export const searchText = r => [r.ra_number, r.activity, r.scope, ...(Array.isArray(r.hazards) ? r.hazards.map(h => [h && h.hazard, h && h.consequence, h && h.category].join(' ')) : [])].join(' ').toLowerCase()
export function matchesSearch(r, q) {
  const words = String(q || '').toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return true
  const t = searchText(r)
  return words.every(w => t.includes(w))
}
// filter = { q, status: '' | review | active | draft | archived, source: '' | library | vessel, dept: '' | Deck ... }
// an RA of department "All" shows under every department; archived ones only when that chip is on
export function applyFilter(rows, f = {}) {
  return (rows || []).filter(r => {
    const k = statusKind(r.status)
    if (f.status ? k !== f.status : k === 'archived') return false
    if (f.source === 'library' && !isLibrary(r)) return false
    if (f.source === 'vessel' && isLibrary(r)) return false
    if (f.dept && f.dept !== 'All') { const d = raDepartment(r); if (d !== f.dept && d !== 'All') return false }
    return matchesSearch(r, f.q)
  })
}
// the count on each chip (the other filters applied, so the numbers add up to what clicking shows)
export function chipCounts(rows, f = {}) {
  const out = { status: {}, source: {}, dept: {} }
  for (const k of ['review', 'active', 'draft', 'archived']) out.status[k] = applyFilter(rows, { ...f, status: k }).length
  for (const k of ['library', 'vessel']) out.source[k] = applyFilter(rows, { ...f, source: k }).length
  for (const k of RA_DEPARTMENTS) out.dept[k] = applyFilter(rows, { ...f, dept: k === 'All' ? '' : k }).length
  return out
}
// groups by category, in the order of RA_CATEGORIES; inside a group by RA number
export function groupByCategory(rows) {
  const by = new Map(RA_CATEGORIES.map(c => [c, []]))
  for (const r of rows || []) by.get(raCategory(r)).push(r)
  const num = r => { const m = /^RA-(\d+)/i.exec(r.ra_number || ''); return m ? Number(m[1]) : 9999 }
  return [...by.entries()].filter(([, l]) => l.length).map(([category, list]) => ({ category, rows: list.sort((a, b) => num(a) - num(b) || String(a.ra_number || '').localeCompare(String(b.ra_number || '')) || String(a.activity || '').localeCompare(String(b.activity || ''))) }))
}

// ---- duplicates: a library RA and a vessel RA for the same activity ----
const SYN = { aloft: 'height', mast: 'height', height: 'height', climbing: 'height', enclosed: 'enclosed', confined: 'enclosed', tank: 'enclosed', tanks: 'enclosed',
  mooring: 'mooring', berthing: 'mooring', anchoring: 'anchor', anchor: 'anchor', windlass: 'anchor', crane: 'lifting', davit: 'lifting', davits: 'lifting', lifting: 'lifting',
  rescue: 'rescue', mob: 'rescue', towing: 'tow', tow: 'tow', diving: 'dive', dive: 'dive', divers: 'dive', generator: 'generator', genset: 'generator',
  alarm: 'alarm', monitoring: 'alarm', guest: 'person', person: 'person', passenger: 'person', supernumerary: 'person' }
const STOP = new Set(['the', 'and', 'for', 'with', 'into', 'from', 'out', 'of', 'on', 'or', 'operations', 'operation', 'entry', 'working', 'work', 'spaces', 'space', 'board', 'service', 'one', 'additional', 'going', 'sea', 'system'])
const keyWords = t => new Set((String(t || '').toLowerCase().match(/[a-z]+/g) || []).filter(w => w.length > 2 && !STOP.has(w)).map(w => SYN[w] || w.replace(/(ing|s)$/, '')))
// two activities are "the same" when they share a key word (after other names: aloft = height, confined = enclosed ...)
export function similarActivity(a, b) {
  const A = keyWords(a), B = keyWords(b)
  for (const w of A) if (B.has(w)) return true
  return false
}
// id -> the other RA ("Also on board: <title>"): a library RA and a vessel RA (manual / AI) of the same activity, neither archived
export function duplicates(rows) {
  const live = (rows || []).filter(r => statusKind(r.status) !== 'archived')
  const lib = live.filter(isLibrary), own = live.filter(r => !isLibrary(r))
  const out = {}
  for (const a of lib) for (const b of own) if (similarActivity(a.activity, b.activity)) { if (!out[a.id]) out[a.id] = b; if (!out[b.id]) out[b.id] = a }
  return out
}

// ---- the person who reviewed (the database stamps it: sql/ra-organise.sql ra_reviewer_name) ----
// "Captain" alone = an older stamp of the vessel account: show the account's own name when the page knows it
// the assessor of a new / adopted RA: the person drafting it, name + role, as the database names a reviewer (ra_reviewer_name ->
// ra_person_name, sql/ra-organise.sql). Before that answers: the login's crew name + role. A bare "Captain" (no name known) = ''.
export function assessorLabel(dbName, cfg) {
  const n = String(dbName || '').trim()
  if (n && !/^captain$/i.test(n)) return n
  const c = cfg || {}, nm = String(c.crewName || '').trim()
  if (!nm) return ''
  const role = String(c.role || 'crew').replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())
  return /\(.*\)\s*$/.test(nm) ? nm : nm + ' (' + role + ')'
}
export function reviewerLabel(name, ownerName) {
  const n = String(name || '').trim()
  if (!n) return ''
  if (/^captain$/i.test(n) && ownerName) return ownerName + ' (Captain)'
  return n
}

// ---- regulatory basis: only the regulations library, flag state from the vessel's flag ----
const RED_ENSIGN = /\b(united kingdom|uk|great britain|british|red ensign|cayman|bermuda|british virgin|bvi|gibraltar|isle of man|jersey|guernsey|falkland|anguilla|montserrat|st\.? helena|turks)\b/i
export function flagKey(flag) {
  const f = String(flag || '')
  if (/marshall/i.test(f)) return 'Marshall Islands'
  if (/\bmalt(a|ese)\b/i.test(f)) return 'Malta'
  if (RED_ENSIGN.test(f)) return 'Red Ensign'
  return ''
}
const isCayman = flag => /cayman/i.test(String(flag || ''))
// the codes that exist in the library (regs = [{ code }]); with no library known (offline, a dry run) the list stays as it is
export const inLibrary = (codes, regs) => { const have = regs && regs.length ? new Set(regs.map(r => r.code)) : null; return [...new Set((codes || []).filter(c => c && (!have || have.has(c))))] }
// the flag-state codes of a vessel: the file's flag table (lib.flag_state) and the RA's own extras for that flag, in the library only
export function flagStateCodes(flag, fileFlags, extra, regs) {
  const k = flagKey(flag), out = [...((fileFlags && k && fileFlags[k]) || [])]
  if (extra) for (const [name, codes] of Object.entries(extra)) if (name === k || (name === 'Cayman Islands' && isCayman(flag))) out.push(...codes)
  return inLibrary(out, regs)
}
// a library RA's regulatory basis for one vessel
export function libraryBasis(item, ctx = {}, fileFlags) {
  const rb = (item && item.regulatory_basis) || {}
  return { international_uk: inLibrary(rb.international_uk, ctx.regs), flag_state: flagStateCodes(ctx.flag, fileFlags, rb.flag_state_extra, ctx.regs) }
}
// the AI: the regulations the generator may use for this vessel - the library minus the flag codes of other flags
export function regsForFlag(regs, flag) {
  const k = flagKey(flag)
  return (regs || []).filter(r => {
    if (!/flag/i.test(r.category || '')) return true
    const n = (r.code || '') + ' ' + (r.full_name || '')
    return !!k && (k === 'Marshall Islands' ? /marshall/i.test(n) : k === 'Malta' ? /malta/i.test(n) : /red ensign|\bREG\b/i.test(n))
  })
}
// a citation in brackets "(MGN 659)" -> is it one of these codes? (a short form of a long code counts)
const norm = s => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim()
export const citesCode = (cite, codes) => (codes || []).some(c => { const a = norm(cite), b = norm(c); return a === b || b.startsWith(a + ' ') || b.startsWith(a + '(') || a.startsWith(b) })
const REG_LIKE = /\b(MGN|MSN|MIN|SOLAS|MARPOL|STCW|MLC|COSWP|LOLER|PUWER|COSHH|DSEAR|ISM|ISPS|COLREGS?|MI-\d|CYC|REG|CISN|CIGN|LSA Code|FSS Code|IMDG|PSSR|EN \d|BS \d|ISO \d|Work at Height|Electricity at Work)/i
// the bracketed citations of a text
export const citations = t => [...String(t || '').matchAll(/\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g)].map(m => m[1].trim()).filter(c => REG_LIKE.test(c))
// drop a bracketed regulation that is not one of these codes (the AI may not cite what the library does not have)
export function keepCitations(text, codes) {
  return String(text || '').replace(/\s*\(([^()]*(?:\([^()]*\)[^()]*)*)\)/g, (all, inner) => {
    if (!REG_LIKE.test(inner)) return all
    const ok = inner.split(/[;,]\s*/).filter(p => citesCode(p, codes))
    return ok.length ? ' (' + ok.join('; ') + ')' : ''
  })
}

// ---- related documents & cross-references ----
// kind -> the Hey Captain page it opens
export const RD_PAGES = { checklist: 'checklists', equipment: 'equipment', crew_certs: 'crew_certs', risk_assessment: 'risk_assessments', sms: 'manuals', manual: 'manuals', drills: 'drill_matrix', lsa: 'lsa', permit: 'checklists', contingency: 'checklists' }
const RD_RULES = [[/pre-?job checklist|checklist/i, 'checklist'], [/risk assessment|^\s*RA-\d+/i, 'risk_assessment'], [/equipment register|equipment|planned maintenance|calibration/i, 'equipment'],
  [/training|certificat|competenc|crew cert/i, 'crew_certs'], [/drill/i, 'drills'], [/\bLSA\b|life-?saving|\bFFE\b|fire-?fighting|lifejacket|liferaft/i, 'lsa'], [/contingency/i, 'contingency'],
  [/permit/i, 'permit'], [/\bSMS\b|procedure|manual|policy|section|emergency response|muster/i, 'sms']]
// one cross reference (a string "Title - note" from the AI or an older RA, or an object) -> { kind, title, note, q, code, id }
export function parseRef(x) {
  if (x && typeof x === 'object') return { kind: x.kind || refKind((x.title || '') + ' ' + (x.note || '')), title: String(x.title || ''), note: String(x.note || ''), q: x.q || '', code: x.code || '', id: x.id || '' }
  const s = String(x || '').trim()
  // the last em / en dash splits title and note, else the first " - ", else the first ": "
  const m = /^(.*)\s+[—–]\s+(.*)$/.exec(s) || /^(.*?)\s+-\s+(.*)$/.exec(s) || /^(.*?):\s+(.*)$/.exec(s)
  let title = m ? m[1] : s, note = m ? m[2] : ''
  // "Risk Assessment - Diving Operations: overlap in rescue equipment" (the generator's form): the RA's name is the title
  if (/^\s*risk assessments?\s*$/i.test(title) && note) { const n = /^(.*?)(?::\s+(.*))?$/.exec(note); title = 'Risk assessment - ' + n[1]; note = n[2] || '' }
  return { kind: refKind(s), title, note, q: '', code: (/^\s*(?:risk assessment\s*[-:—]\s*)?(RA-\d+(?:-A\d+)?)\b/i.exec(s) || [])[1] || '', id: '' }
}
export const refKind = s => { for (const [re, k] of RD_RULES) if (re.test(String(s || ''))) return k; return 'sms' }
// RD-1 ... with their link. ctx = { ras: [{ id, ra_number, activity }], checklist: { id, title } | null, plans: the vessel's Contingency Plans
// [{ id, title }] (checklists of category Contingency Plans, src/lib/contingency.js), pages: Set of page keys this login may open }
// link = { page, q?, id?, checklist?, manual? } (opened in the app) or null when the target is not in Hey Captain (another RA that is not on this vessel)
// A contingency plan opens the vessel's plan of that name (Checklists, its own title), else the Checklists page
export function relatedDocs(refs, ctx = {}) {
  const ras = ctx.ras || []
  return (Array.isArray(refs) ? refs : []).filter(Boolean).map((x, i) => {
    const r = parseRef(x)
    let link = null
    const page = RD_PAGES[r.kind]
    if (r.kind === 'checklist' && ctx.checklist && ctx.checklist.title) link = { page: 'checklists', q: ctx.checklist.title, checklist: ctx.checklist.title }
    else if (r.kind === 'checklist') link = { page: 'checklists', q: r.q || '' }
    else if (r.kind === 'contingency') {
      const words = (r.title + ' ' + r.note).replace(/contingency plans?/i, '')
      const plan = (ctx.plans || []).find(p => similarActivity(p.title, words))
      link = plan ? { page: 'checklists', q: plan.title, checklist: plan.title } : { page: 'checklists', q: '' }
    }
    else if (r.kind === 'risk_assessment') {
      const code = r.code || (/\b(RA-\d+(?:-A\d+)?)\b/i.exec(r.title) || [])[1] || ''
      const words = r.title.replace(/^\s*(risk assessment\s*[-:—]\s*)?(RA-\d+(-A\d+)?)?\s*/i, '')
      const hit = ras.find(o => (code && String(o.ra_number || '').toLowerCase() === code.toLowerCase()) || (words && o.activity && String(o.activity).toLowerCase() === words.toLowerCase().trim()))
        || (words ? ras.find(o => o.activity && similarActivity(o.activity, words) && String(o.activity).toLowerCase().includes(words.toLowerCase().split(/\s+/)[0])) : null)
      if (hit) link = { page: 'risk_assessments', id: hit.id, q: hit.ra_number || hit.activity }
    } else if (r.kind === 'manual' && r.id) link = { page: 'manuals', q: r.title, manual: r.id }   // one of the vessel's manuals (fixes-1 part 4: the manual a draft RA was taken from)
    else if (page) link = { page, q: r.q || '' }
    if (link && ctx.pages && !ctx.pages.has(link.page)) link = null
    return { ref: 'RD-' + (i + 1), ...r, link }
  })
}
// a link -> a URL into the app (the PDF): ?open=go&page=..&q=..&id=.. (src/lib/approvalsCore.js parseDeepLink)
export function appUrl(origin, link) {
  if (!link || !origin) return ''
  const p = new URLSearchParams({ open: 'go', page: link.page })
  if (link.q) p.set('q', String(link.q).slice(0, 120))
  if (link.id) p.set('id', link.id)
  return String(origin).replace(/\/$/, '') + '/?' + p.toString()
}

// ---- hazards grouped A, B, C ... by category (the PDF and the page) ----
export function hazardGroups(hazards) {
  const groups = [], at = {}
  for (const h of (hazards || []).filter(h => h && (h.hazard || h.existing_controls))) {
    const c = String(h.category || '').trim() || 'General'
    if (!(c in at)) { at[c] = groups.length; groups.push({ category: c, hazards: [] }) }
    groups[at[c]].hazards.push(h)
  }
  return groups.map((g, i) => ({ ...g, letter: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[i] || String(i + 1), items: g.hazards.map((h, k) => ({ no: ('ABCDEFGHIJKLMNOPQRSTUVWXYZ'[i] || String(i + 1)) + (k + 1), h })) }))
}

// ---- the standard: what an RA must have (the tests check every library and AI-generated RA with this) ----
// ra: a vessel row (or a library item mapped to one). -> [problems], empty = meets the standard
export function standardProblems(ra) {
  const bad = []
  const rb = ra.regulatory_basis || {}
  if (!(Array.isArray(rb.international_uk) && rb.international_uk.length) && !(Array.isArray(rb.flag_state) && rb.flag_state.length)) bad.push('no regulatory basis')
  if (!(Array.isArray(ra.overarching_controls) && ra.overarching_controls.filter(Boolean).length)) bad.push('no overarching controls')
  if (!RA_CATEGORIES.includes(ra.category)) bad.push('no RA category')
  if (!RA_DEPARTMENTS.includes(ra.department)) bad.push('no department')
  if (!(Array.isArray(ra.cross_references) && ra.cross_references.filter(Boolean).length)) bad.push('no related document')
  const hz = Array.isArray(ra.hazards) ? ra.hazards : []
  if (!hz.length) bad.push('no hazards')
  hz.forEach((h, i) => {
    const w = 'hazard ' + (i + 1) + ': '
    if (!String(h.category || '').trim()) bad.push(w + 'no category')
    if (!ROLES.includes(h.responsible)) bad.push(w + 'no responsible role')
    if (!hazardRisk(h, 'initial') || !hazardRisk(h, 'residual')) bad.push(w + 'not scored on the matrix')
    if ('likelihood' in h || 'severity' in h) bad.push(w + 'old 1-5 x 1-5 numbers')
    const cites = citations(h.additional_controls)
    const codes = [...(rb.international_uk || []), ...(rb.flag_state || [])]
    if (!cites.length) bad.push(w + 'no regulation cited in the additional controls')
    for (const c of cites) if (!c.split(/[;,]\s*/).every(p => citesCode(p, codes))) bad.push(w + 'cites "' + c + '", not in its regulatory basis')
  })
  return bad
}
// the hazards of two versions are the same content (old numbers vs codes count as the same): the page keeps the stored hazards then,
// so opening and saving an Active RA never looks like an edit to the Master's review (sql/ra-reapprove.sql)
export function sameHazards(a, b) {
  const k = h => JSON.stringify({ ...Object.fromEntries(Object.entries(h || {}).filter(([x]) => !['likelihood', 'severity', 'residual_likelihood', 'residual_severity', 'initial_score', 'residual_score', 'initial_risk', 'residual_risk', 'actions'].includes(x)).sort()), i: hazardCode(h, 'initial'), r: hazardCode(h, 'residual') })
  return (a || []).length === (b || []).length && (a || []).every((h, i) => k(h) === k(b[i]))
}
